import { useState } from 'react'
import { ImportSession, DataEntity, ConflictStrategy } from '@retailsync/shared-types'
import type { ConnectorEngine } from '../../services/connector-engine'
import { importData } from '../../services/connectorApi'
import { useAuth } from '../../contexts/AuthContext'

interface ImportStepProps {
  session: ImportSession
  onUpdate: (updates: Partial<ImportSession>) => void
  engine: ConnectorEngine | null
  setIsProcessing: (v: boolean) => void
}

export function ImportStep({ session, onUpdate, engine, setIsProcessing }: ImportStepProps) {
  const { tenant } = useAuth()
  const [processing, setProcessing] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [result, setResult] = useState<{
    totalRecords: number
    processedRecords: number
    failedRecords: number
    duration: number
    apiResult?: { imported: number; failed: number; skipped: number }
  } | null>(null)
  const [apiErrors, setApiErrors] = useState<Array<{ row: number; field?: string; message: string }>>([])

  const handleStartImport = async () => {
    if (!session.rawData || !session.mappings || !session.selectedTemplate) {
      setError('Missing data. Please complete all previous steps first.')
      return
    }

    setProcessing(true)
    setError(null)
    setApiErrors([])
    setIsProcessing(true)

    const startTime = Date.now()

    try {
      const entity = (session.selectedTemplate.entity || 'products') as DataEntity
      const strategy: ConflictStrategy = 'external_wins'
      const previewData = session.previewData || session.rawData

      // ── Step 1: Local pre-flight (conflict/validation simulation only) ──
      if (engine) {
        try {
          await engine.executeSync(entity, 'custom', previewData, strategy)
        } catch (e) {
          console.warn('[Import] Local engine pre-flight failed:', e)
        }
      }

      // ── Step 2: Real API import (this is the write that actually matters) ──
      if (!tenant?.id) {
        throw new Error('No tenant context — sign in again before importing.')
      }
      const resp = await importData({
        tenantId: tenant.id,
        branchId: undefined,
        entity,
        records: previewData,
        conflictStrategy: strategy,
        dryRun: false,
      })
      const apiResult = { imported: resp.imported, failed: resp.failed, skipped: resp.skipped }
      if (resp.errors?.length) {
        setApiErrors(resp.errors.slice(0, 20))
      }
      // The server reports a partial failure with 207 — never claim success then.
      if (resp.failed > 0 && resp.imported === 0) {
        throw new Error(
          `Server rejected every row (${resp.failed} failed). ${resp.errors?.[0]?.message || 'Check your field mappings.'}`,
        )
      }

      setResult({
        totalRecords: previewData.length,
        processedRecords: apiResult.imported,
        failedRecords: apiResult.failed,
        duration: Date.now() - startTime,
        apiResult,
      })
      onUpdate({ importComplete: true })
    } catch (err) {
      console.error('Import error:', err)
      setError(err instanceof Error ? err.message : 'Import failed')
    } finally {
      setProcessing(false)
      setIsProcessing(false)
    }
  }

  if (result && !error) {
    return (
      <div className="space-y-6">
        <div className="text-center py-8">
          <div className="text-6xl mb-4">✅</div>
          <h3 className="text-xl font-bold text-green-800 mb-2">Import Complete!</h3>
          <p className="text-gray-600">
            Your data has been processed and is ready for use in RetailsyncGo.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div className="bg-white border border-gray-200 rounded-lg p-4 text-center">
            <div className="text-2xl font-bold text-blue-600">{result.totalRecords}</div>
            <div className="text-sm text-gray-500">Total Records</div>
          </div>
          <div className="bg-white border border-gray-200 rounded-lg p-4 text-center">
            <div className="text-2xl font-bold text-green-600">{result.processedRecords}</div>
            <div className="text-sm text-gray-500">Imported</div>
          </div>
          <div className="bg-white border border-gray-200 rounded-lg p-4 text-center">
            <div className="text-2xl font-bold text-red-600">{result.failedRecords}</div>
            <div className="text-sm text-gray-500">Failed</div>
          </div>
          <div className="bg-white border border-gray-200 rounded-lg p-4 text-center">
            <div className="text-2xl font-bold text-purple-600">{(result.duration / 1000).toFixed(1)}s</div>
            <div className="text-sm text-gray-500">Duration</div>
          </div>
        </div>

        {result.apiResult && (
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 text-sm text-blue-800">
            <strong>Server:</strong> {result.apiResult.imported} imported, {result.apiResult.skipped} skipped, {result.apiResult.failed} failed
          </div>
        )}

        {apiErrors.length > 0 && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-4 max-h-48 overflow-y-auto">
            <h4 className="text-sm font-semibold text-red-800 mb-2">Server Errors ({apiErrors.length})</h4>
            <ul className="text-xs text-red-700 space-y-1">
              {apiErrors.map((e, i) => (
                <li key={i}>Row {e.row}: {e.field} — {e.message}</li>
              ))}
            </ul>
          </div>
        )}

        <div className="flex justify-center">
          <button
            onClick={() => onUpdate({ currentStep: 'upload' })}
            className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
          >
            Start New Import
          </button>
        </div>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      <div className="text-center py-12">
        <h3 className="text-lg font-medium text-gray-900 mb-2">Execute Import</h3>
        <p className="text-gray-600 mb-6">
          Your data has been mapped and previewed. Click below to run the import pipeline locally.
        </p>

        {session.rawData && (
          <div className="mb-6 inline-block p-4 bg-green-50 border border-green-200 rounded-lg">
            <p className="text-sm text-green-800">
              ✅ {session.rawData.length} records ready to import
            </p>
            {session.mappings && (
              <p className="text-xs text-green-600 mt-1">
                {session.mappings.filter(m => m.sourceField).length} field mappings configured
              </p>
            )}
          </div>
        )}

        {error && (
          <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg text-left">
            <div className="flex items-start">
              <div className="flex-shrink-0">
                <svg className="h-5 w-5 text-red-400" viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                </svg>
              </div>
              <div className="ml-3">
                <h3 className="text-sm font-medium text-red-800">Import Failed</h3>
                <p className="text-sm text-red-700 mt-1">{error}</p>
                <button
                  onClick={handleStartImport}
                  className="mt-3 px-3 py-1 bg-red-600 text-white text-sm rounded hover:bg-red-700"
                >
                  Retry
                </button>
              </div>
            </div>
          </div>
        )}

        <button
          onClick={handleStartImport}
          disabled={processing || !session.rawData}
          className="px-8 py-3 bg-blue-600 text-white rounded-lg font-medium hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {processing ? (
            <>
              <span className="inline-block animate-spin rounded-full h-4 w-4 border-2 border-white border-b-transparent mr-2"></span>
              Importing...
            </>
          ) : (
            'Start Import'
          )}
        </button>
      </div>
    </div>
  )
}